/**
 * Провайдеры приложения: кэш запросов, его офлайн-персист и досылка очереди.
 *
 * Персист ключуется по пользователю и стирается при выходе — механика и
 * обоснование в lib/offline/cache.ts.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { QueryClient, focusManager, onlineManager } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { Toaster, toast } from 'sonner'
import { createPersister, noopPersister } from '@/lib/offline/cache'
import { pageBusy, watchForNewBuild } from '@/lib/app-update'
import {
  drainOutbox,
  rejectedText,
  rejectedTone,
  setOutboxUser,
  subscribeOutbox,
} from '@/lib/offline/outbox'
import { useAuth } from '@/lib/auth'
import { QK } from '@/api/scout'
import { VQK } from '@/api/vehicles'
import { PQK } from '@/api/pool'
import { CQK } from '@/api/collector'

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Досылка + ПОКАЗ ОТКЛОНЁННОГО.
 *
 * drainOutbox возвращает записи, которые сервер отверг по существу, и в его
 * шапке прямо сказано, что интерфейс обязан их показать. До этого места он их
 * не показывал: результат отбрасывался через `void drainOutbox()`.
 *
 * Пока в очереди были только задачи, цена молчания была невелика — задачу,
 * которую взял другой, скаут и так увидит пропавшей. Со сменой статуса она
 * другая: «пометил сломанным» отклоняется по STATUS_TRANSITION_INVALID (двое
 * приехали на один байк), и без сообщения скаут уверен, что байк снят с линии.
 *
 * Тост, а не плашка: отказ приходит асинхронно, когда появилась сеть, и
 * привязывать его к экрану, с которого действие поставили, нельзя — скаут
 * давно на другом.
 */
function flushOutbox(client: QueryClient): void {
  void drainOutbox().then((rejected) => {
    /*
     * ⚠ БЕЗ ЭТОГО «ЗАВЕРШИТЬ» НЕ ПОЯВЛЯЕТСЯ.
     *
     * Задача рождается на сервере: ответ досылки очередь отбрасывает, id новой
     * задачи взять неоткуда, и единственный способ её увидеть — перечитать
     * «мои». Инвалидация при постановке в очередь (в onSuccess мутации) этого
     * не делает: на сервере в тот момент задачи ещё нет, и перечитывание
     * вернуло бы прежний список. Без строки ниже исполнитель, нажавший
     * «Переставить», ждал бы кнопку «Завершить» до истечения staleTime — то
     * есть до полуминуты, стоя у байка.
     *
     * Безусловно, а не только при отправленных: drainOutbox возвращает лишь
     * отклонённое, и отличить «ушло» от «очередь была пуста» здесь нечем.
     */
    void client.invalidateQueries({ queryKey: QK.mine })

    /*
     * ⚠ И ПАРК ТОЖЕ, ПО ТОЙ ЖЕ ПРИЧИНЕ, ЧТО И «МОИ».
     *
     * Смена статуса рисуется оптимистично (`api/vehicles.ts`), то есть до
     * ответа сервера на экране стоит НАШЕ предположение. Приговор выносит
     * сервер, и выносит он его здесь. Без этой строки отклонённый переход
     * (двое скаутов приехали на один байк) оставлял бы на карте статус,
     * которого на сервере нет, до истечения staleTime.
     */
    void client.invalidateQueries({ queryKey: VQK.vehicles })

    // Пул водителя: «Забрал»/«Поставил» меняют его только на сервере, а
    // ответ досылки очередь отбрасывает.
    void client.invalidateQueries({ queryKey: PQK.mine })

    // Сборщик: «Отменить» вернул велик в ленту только на сервере.
    void client.invalidateQueries({ queryKey: CQK.tasks })

    for (const entry of rejected) {
      const text = rejectedText(entry)
      // Не всякий отказ — сбой. «Задача на этот байк уже есть» означает, что
      // нужное состояние уже достигнуто, просто не нами; красный тост здесь
      // приучает не читать красные тосты вообще. См. rejectedTone.
      if (rejectedTone(entry) === 'info') toast(text)
      else toast.error(text)
    }
  })
}

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Полевой интернет: одна повторная попытка, дальше показываем кэш.
        retry: 1,
        // Восстановленный из IndexedDB кэш должен пережить перезапуск в подвале.
        gcTime: WEEK_MS,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: { retry: 0 },
    },
  })
}

export function Providers({ children }: { children: ReactNode }) {
  const { userId } = useAuth()
  const [client] = useState(makeQueryClient)

  /*
   * Очередь принадлежит пользователю, как и кэш чтения.
   *
   * Ставится ДО первой досылки: иначе на старте она читалась бы из общего
   * ключа и могла отправить чужие действия под токеном вошедшего. Смена
   * пользователя без выхода — штатный случай на сменном телефоне парка.
   */
  useEffect(() => {
    void setOutboxUser(userId ?? '')
  }, [userId])

  // Досылаем очередь на старте и как только появилась сеть.
  useEffect(() => {
    flushOutbox(client)
    return onlineManager.subscribe((online) => {
      if (online) flushOutbox(client)
    })
  }, [client])

  /*
   * ⚠ ДОСЫЛКА СРАЗУ ПОСЛЕ НАЖАТИЯ. Без неё приложение выглядело сломанным.
   *
   * Остальные три повода дослать — старт, появление сети и возврат фокуса —
   * не наступают в самом обычном сценарии: скаут открыл приложение, сеть есть
   * всё время (событие `online` стреляет только на ПЕРЕХОДЕ из офлайна), из
   * вкладки он не уходил. Нажатие «Взять» клало запись в IndexedDB, и на этом
   * всё: запрос не уходил, оптимистичного обновления нет, экран не менялся.
   * Со стороны — кнопка не работает.
   *
   * Подписка, а не вызов внутри `enqueue`: очередь не должна знать про
   * onlineManager, а четыре мутации не должны каждая помнить про досылку —
   * это ровно то место, где они разъедутся.
   *
   * Сравнение с прошлой длиной обязательно: notify() срабатывает и на
   * записях, которые делает сам drain(), и безусловная досылка гоняла бы
   * цикл. Повторный вход в drainOutbox и так no-op, но полагаться на это как
   * на единственный ограничитель нельзя.
   */
  useEffect(() => {
    let seen = -1
    return subscribeOutbox((entries) => {
      const grew = seen >= 0 && entries.length > seen
      seen = entries.length
      if (grew && onlineManager.isOnline()) flushOutbox(client)
    })
  }, [client])

  // Возврат из фона — тоже повод дослать: Android усыпляет WebView.
  useEffect(
    () =>
      focusManager.subscribe((focused) => {
        if (focused && onlineManager.isOnline()) flushOutbox(client)
      }),
    [client],
  )

  /*
   * Новая сборка на сервере — перезагрузка, но не посреди действия: летящая
   * команда замку (мутация) или открытый лист/ввод (`pageBusy`). Очередь
   * действий в IndexedDB перезагрузку переживает.
   */
  useEffect(() => {
    if (!import.meta.env.PROD) return
    return watchForNewBuild({ isBusy: () => client.isMutating() > 0 || pageBusy(document) })
  }, [client])

  const persister = userId ? createPersister(userId) : noopPersister

  return (
    <PersistQueryClientProvider client={client} persistOptions={{ persister, maxAge: WEEK_MS }}>
      {children}
      {/* Единственный тостер приложения — сюда же приходят ошибки команд замку
          и фонарику. richColors: на солнце нужен цвет, а не оттенок серого. */}
      <Toaster theme="dark" position="top-center" richColors closeButton />
    </PersistQueryClientProvider>
  )
}
